/**
 * Utility for formatting decimal quantities as fractions for display.
 * Converts decimals to vulgar fractions (Unicode) or mixed numbers.
 * Based on backend/planner/utils/fraction_formatter.py
 */

class FractionFormatter {
  // Mapping of fractions to their Unicode vulgar fraction characters
  private static readonly VULGAR_FRACTIONS: Record<string, string> = {
    "1/2": "½", // U+00BD
    "1/3": "⅓", // U+2153
    "2/3": "⅔", // U+2154
    "1/4": "¼", // U+00BC
    "3/4": "¾", // U+00BE
    "1/5": "⅕", // U+2155
    "2/5": "⅖", // U+2156
    "3/5": "⅗", // U+2157
    "4/5": "⅘", // U+2158
    "1/6": "⅙", // U+2159
    "5/6": "⅚", // U+215A
    "1/8": "⅛", // U+215B
    "3/8": "⅜", // U+215C
    "5/8": "⅝", // U+215D
    "7/8": "⅞", // U+215E
  };

  /**
   * Format a number as a fraction or mixed number.
   * @param number - The number to format
   * @param tolerance - Tolerance for fraction approximation (default 0.012)
   * @returns Formatted string with fractions where appropriate
   */
  public static formatQuantity(number: number | null | undefined, tolerance = 0.012): string {
    if (number === null || number === undefined) {
      return "";
    }

    // Handle zero
    if (number === 0) {
      return "0";
    }

    // Handle negative numbers
    const negative = number < 0;
    const absNumber = Math.abs(number);

    // Split into whole and fractional parts
    const wholePart = Math.floor(absNumber);
    const fractionalPart = absNumber - wholePart;

    // If it's already a whole number, return it
    if (fractionalPart < tolerance) {
      const result = wholePart.toString();
      return negative ? `-${result}` : result;
    }

    // Always use fraction representation
    const fractionStr = this.formatFraction(fractionalPart, tolerance);

    // If the fraction rounds to 0, just return the whole part
    let result: string;
    if (fractionStr === "0") {
      result = wholePart.toString();
    } else if (wholePart === 0) {
      result = fractionStr;
    } else {
      // For mixed numbers, check if it's a vulgar fraction
      const isVulgarFraction = /[½⅓⅔¼¾⅕⅖⅗⅘⅙⅚⅛⅜⅝⅞]/.test(fractionStr);
      if (isVulgarFraction) {
        // Vulgar fractions go directly after the whole number
        result = `${wholePart}${fractionStr}`;
      } else {
        // Regular fractions need a space
        result = `${wholePart} ${fractionStr}`;
      }
    }

    return negative ? `-${result}` : result;
  }

  /**
   * Format the fractional part of a number.
   * @param decimal - The fractional part (0 < decimal < 1)
   * @param tolerance - Tolerance for fraction approximation
   * @returns Formatted fraction string
   */
  private static formatFraction(decimal: number, tolerance: number): string {
    // Handle very small amounts - round to 0
    if (decimal < 0.0625) {
      // Less than 1/16
      return "0";
    }

    // Find the closest common cooking fraction
    let bestFraction: string | null = null;
    let minDifference = Infinity;

    // Check all vulgar fractions to find the closest match
    Object.entries(this.VULGAR_FRACTIONS).forEach(([fractionKey, vulgar]) => {
      const parts = fractionKey.split("/");
      const numerator = Number(parts[0]);
      const denominator = Number(parts[1]);
      const target = numerator / denominator;
      const difference = Math.abs(decimal - target);

      if (difference < minDifference) {
        minDifference = difference;
        bestFraction = vulgar;
      }
    });

    // If we found a close vulgar fraction, return it
    if (bestFraction && minDifference <= 0.1) {
      // More generous than tolerance for rounding
      return bestFraction;
    }

    // Otherwise, find the closest simple fraction with common cooking denominators
    let bestNumerator = 0;
    let bestDenominator = 1;
    let minFracDiff = Infinity;
    let foundFraction = false;

    [2, 3, 4, 5, 6, 8].forEach((denominator) => {
      // Find the closest numerator for this denominator
      const numerator = Math.round(decimal * denominator);
      if (numerator === 0 || numerator === denominator) {
        return;
      }

      const fracValue = numerator / denominator;
      const difference = Math.abs(fracValue - decimal);

      if (difference < minFracDiff) {
        minFracDiff = difference;
        bestNumerator = numerator;
        bestDenominator = denominator;
        foundFraction = true;
      }
    });

    if (foundFraction) {
      // Check if we have a vulgar fraction for this
      const fractionKey = `${bestNumerator}/${bestDenominator}`;
      const vulgar = this.VULGAR_FRACTIONS[fractionKey];
      if (vulgar) {
        return vulgar;
      }
      // Otherwise return as a regular fraction
      return fractionKey;
    }

    // As a last resort (shouldn't happen with common cooking measurements)
    // Return the closest half
    if (decimal < 0.25) {
      return "¼";
    }
    if (decimal < 0.5) {
      return "½";
    }
    if (decimal < 0.75) {
      return "¾";
    }
    return "⅞";
  }

  /**
   * Format a quantity range (e.g., 1/2-3/4 cup).
   * @param qty1 - Start of range
   * @param qty2 - End of range
   * @param tolerance - Tolerance for fraction approximation
   * @returns Formatted range string
   */
  public static formatRange(
    qty1: number | null | undefined,
    qty2: number | null | undefined,
    tolerance = 0.012
  ): string {
    if (qty1 === null || qty1 === undefined) {
      return "";
    }

    const formatted1 = this.formatQuantity(qty1, tolerance);

    if (qty2 === null || qty2 === undefined || qty2 === qty1) {
      return formatted1;
    }

    const formatted2 = this.formatQuantity(qty2, tolerance);
    return `${formatted1}-${formatted2}`;
  }
}

export default FractionFormatter;
